import { useEffect, useRef, useState } from 'react';
import { fullReportLabel, trackFullReportCta } from './NatalPreviewBlocks';

/**
 * Воронка гостя после расчёта карты: закреплённая кнопка на телефоне.
 *
 * Зачем. По Метрике (natal_free, 18–19.09) карту получили 77 человек,
 * кнопку «Открыть весь разбор» нажал 21 — терялось 73 %. Со смартфонов
 * регистрировались втрое хуже, чем с ПК (5 % визитов против 15 %). Сама
 * форма в окне работает: из 10 отправивших зарегистрировались 9. Значит
 * теряет не форма, а дорога до неё — человек не доходит до кнопки.
 *
 * Поэтому здесь остался один приём: на телефоне кнопка едет за человеком по
 * низу экрана и ведёт в ТО ЖЕ окно регистрации одной строкой
 * (NatalLimitModal) — новых форм и новых текстов не заводим.
 *
 * Второго приёма — окна, которое приходило САМО, когда человек долистал
 * полосу разбора и завис, — больше нет: решение владельца, на пути гостя
 * никакой рекламы, человек регистрируется сам. Вместе с ним ушли отметка
 * «окно уже видели в этом визите» (sessionStorage) и хук useAutoWall.
 */

const NARROW_MQ = '(max-width: 767px)';   // тот же порог, что md: в Tailwind

function matchesNarrow() {
  try {
    return window.matchMedia(NARROW_MQ).matches;
  } catch (_e) {
    return false;
  }
}

/**
 * Следим за элементом, которого в разметке ещё может не быть.
 *
 * Полоса превью приезжает с сервера, а кнопка разбора — вместе с ней, то
 * есть позже монтирования. Поэтому селектор опрашивается, пока элемент не
 * появится, и только тогда на него встаёт IntersectionObserver.
 *
 * holdMs === 0 — отдаём булево «видно сейчас».
 * holdMs > 0   — зовём onHold, когда элемент НЕПРЕРЫВНО пробыл в кадре
 *                столько миллисекунд. Ушёл из кадра — отсчёт сбрасывается.
 */
export function useWatchedVisibility(selector, { enabled = true, holdMs = 0, onHold } = {}) {
  const [onScreen, setOnScreen] = useState(false);
  // Колбэк в ref: иначе новая функция на каждый рендер родителя
  // пересобирала бы наблюдатель и обнуляла отсчёт, и три секунды не
  // набрались бы никогда.
  const onHoldRef = useRef(onHold);
  onHoldRef.current = onHold;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setOnScreen(false);
      return undefined;
    }
    if (typeof IntersectionObserver === 'undefined') return undefined;

    let cancelled = false;
    let io = null;
    let holdTimer = 0;
    let findTimer = 0;
    let tries = 0;

    const clearHold = () => {
      if (holdTimer) {
        window.clearTimeout(holdTimer);
        holdTimer = 0;
      }
    };

    const attach = (el) => {
      io = new IntersectionObserver((entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        setOnScreen(visible);
        if (!holdMs) return;
        if (visible && !holdTimer) {
          holdTimer = window.setTimeout(() => {
            holdTimer = 0;
            if (!cancelled && typeof onHoldRef.current === 'function') onHoldRef.current();
          }, holdMs);
        } else if (!visible) {
          clearHold();
        }
      }, { threshold: 0 });
      io.observe(el);
    };

    const find = () => {
      if (cancelled) return;
      const el = document.querySelector(selector);
      if (el) {
        attach(el);
        return;
      }
      if (tries++ > 100) return;   // ~20 с и сдаёмся: элемент так и не приехал
      findTimer = window.setTimeout(find, 200);
    };
    find();

    return () => {
      cancelled = true;
      clearHold();
      if (findTimer) window.clearTimeout(findTimer);
      if (io) io.disconnect();
      // Состояние здесь НЕ трогаем: на размонтировании это была бы
      // запись в мёртвый компонент. Сброс делает ветка !enabled выше —
      // эффект перезапускается на любое изменение enabled.
    };
  }, [selector, enabled, holdMs]);

  return onScreen;
}

/**
 * Поднята ли экранная клавиатура.
 *
 * Полоса стоит по низу экрана, а клавиатура выезжает оттуда же: вместе они
 * закрывают пол-экрана и накрывают поле, в которое человек как раз печатает
 * (на главной под разбором остаётся форма расчёта).
 *
 * Два признака, потому что одного мало:
 *   — фокус в поле ввода. Самый надёжный признак на телефоне: клавиатура
 *     выезжает именно под фокус;
 *   — visualViewport заметно ниже окна. Ловит случаи, когда фокус ушёл в
 *     нестандартный элемент (contenteditable, свой выпадающий список).
 *
 * На focusout проверяем не сразу: браузер снимает фокус ДО того, как отдаст
 * его следующему полю, и переход между двумя полями иначе мигал бы полосой.
 */
function useKeyboardOpen(enabled) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setOpen(false);
      return undefined;
    }
    const FIELDS = /^(INPUT|TEXTAREA|SELECT)$/;
    const focusInField = () => {
      const el = document.activeElement;
      return !!el && (FIELDS.test(el.tagName || '') || el.isContentEditable === true);
    };
    const viewportShrunk = () => {
      const vv = window.visualViewport;
      if (!vv || !window.innerHeight) return false;
      // Клавиатура съедает заметно больше, чем адресная строка браузера.
      return vv.height < window.innerHeight * 0.75;
    };

    let settle = 0;
    const apply = () => setOpen(focusInField() || viewportShrunk());
    const applySoon = () => {
      window.clearTimeout(settle);
      settle = window.setTimeout(apply, 50);
    };
    apply();

    document.addEventListener('focusin', apply);
    document.addEventListener('focusout', applySoon);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', apply);
      vv.addEventListener('scroll', apply);
    }
    return () => {
      window.clearTimeout(settle);
      document.removeEventListener('focusin', apply);
      document.removeEventListener('focusout', applySoon);
      if (vv) {
        vv.removeEventListener('resize', apply);
        vv.removeEventListener('scroll', apply);
      }
    };
  }, [enabled]);

  return open;
}

/**
 * Закреплённая кнопка разбора по низу экрана. Только гость, только узкий
 * экран, только после расчёта карты.
 *
 * Прячется в четырёх случаях:
 *   — настоящая кнопка «Открыть весь разбор» видна в кадре: два одинаковых
 *     призыва рядом друг друга гасят;
 *   — открыто окно регистрации (suppressed);
 *   — поднята экранная клавиатура (useKeyboardOpen);
 *   — человек вошёл — тогда компонент вообще не рендерится родителем.
 *
 * Текст и цель — общие с настоящей кнопкой (fullReportLabel и
 * trackFullReportCta в NatalPreviewBlocks): одна подпись, одно число, одна
 * цель natal_preview_cta, различаются только place.
 *
 * Не перекрывает круглую кнопку помощи (ScenarioBot, fixed z-40): пока
 * полоса видна, значок помощи поднимается НАД ней на её высоту — он читает
 * ту же --natal-sticky-h, что отдаётся ниже. Поэтому угол под значок полоса
 * больше не освобождает и занимает всю ширину. Свой z-30 — ниже помощи, так
 * что даже при неожиданной вёрстке полоса окажется ПОД ней.
 *
 * Высоту полосы отдаём наверх переменной --natal-sticky-h: страница делает
 * на неё отступ снизу, иначе полоса закрыла бы последние строки разбора.
 */
export function StickyFullReportCta({ total, onOpen, suppressed = false }) {
  const barRef = useRef(null);
  const [narrow, setNarrow] = useState(matchesNarrow);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let mq;
    try {
      mq = window.matchMedia(NARROW_MQ);
    } catch (_e) {
      return undefined;
    }
    const apply = () => setNarrow(mq.matches);
    apply();
    // addEventListener появился не во всех живых браузерах — оставляем
    // старый addListener запасным.
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else if (mq.removeListener) mq.removeListener(apply);
    };
  }, []);

  // Настоящая кнопка разбора в кадре — полосу прячем. Наблюдаем только
  // пока полоса вообще может показаться, чтобы не держать таймер зря.
  const mayShow = narrow && total > 0 && !suppressed;
  const ctaOnScreen = useWatchedVisibility('[data-testid="natal-preview-cta"]', {
    enabled: mayShow,
  });
  // Поднятая клавиатура — третий случай, когда полосы быть не должно: она
  // накрыла бы поле, в которое человек печатает. Клавиатура убралась —
  // полоса возвращается сама.
  const keyboardOpen = useKeyboardOpen(mayShow);
  const shown = mayShow && !ctaOnScreen && !keyboardOpen;

  useEffect(() => {
    const root = document.documentElement;
    const el = barRef.current;
    if (!shown || !el) {
      root.style.setProperty('--natal-sticky-h', '0px');
      return undefined;
    }
    const apply = () => root.style.setProperty('--natal-sticky-h', `${el.offsetHeight}px`);
    apply();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(apply);
      ro.observe(el);
    }
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      if (ro) ro.disconnect();
      root.style.setProperty('--natal-sticky-h', '0px');
    };
  }, [shown]);

  if (!shown) return null;

  return (
    <div
      ref={barRef}
      className="fixed inset-x-0 bottom-0 z-30"
      data-testid="natal-sticky-cta-bar"
    >
      {/* Отступа под круглую кнопку помощи здесь больше нет: пока полоса
          видна, значок стоит НАД ней (ScenarioBot читает --natal-sticky-h).
          Освобождённые 72px уходят кнопке — на 360px это целая строка
          текста. */}
      <div
        className="border-t border-slate-200 bg-white/95 px-3 py-2 backdrop-blur"
        style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <button
          type="button"
          onClick={() => {
            // Та же цель, что у настоящей кнопки, — место различает place.
            trackFullReportCta('sticky_mobile');
            if (typeof onOpen === 'function') onOpen();
          }}
          className="w-full rounded-lg bg-sky-600 px-4 py-2 text-[13px] font-semibold leading-snug text-white shadow-md transition-colors hover:bg-sky-700"
          data-testid="natal-sticky-cta"
        >
          {fullReportLabel(total)}
        </button>
      </div>
    </div>
  );
}
